import { PageOptions } from '@graphcommerce/framer-next-pages'
import { GetStaticProps } from '@graphcommerce/next-ui'
import { GetStaticPaths } from 'next'
import { LayoutNavigation, LayoutNavigationProps } from '../../../components'
import { PublishedPage } from '../../../components/PublishedPage/PublishedPage'
import { AuthorByline, getAuthorById } from '../../../lib/content/authors'
import { fetchPublishedPage, PublishedPageContent } from '../../../lib/content/fetchPublishedPage'
import { getPublishedPageLayoutProps } from '../../../lib/content/getPublishedPageLayoutProps'
import { getSectionLabel, isValidSection, publishedPagePath } from '../../../lib/content/sections'

type RouteParams = { section: string; slug: string[] }
type Props = { page: PublishedPageContent; author?: AuthorByline; sectionLabel?: string }
type GetPageStaticProps = GetStaticProps<LayoutNavigationProps, Props, RouteParams>

function SectionPage(props: Props) {
  const { page, author, sectionLabel } = props

  return (
    <PublishedPage
      h1={page.h1}
      heroDescription={page.heroDescription}
      heroImage={page.heroImage}
      metaTitle={page.metaTitle}
      metaDescription={page.metaDescription}
      canonicalPath={publishedPagePath(page.section, page.slug)}
      author={author}
      eyebrow={sectionLabel}
      publishedAt={page.publishedAt}
      blocks={page.blocks}
      toolEmbedUrl={page.toolEmbedUrl}
    />
  )
}

SectionPage.pageOptions = {
  Layout: LayoutNavigation,
} as PageOptions

export default SectionPage

// Pages are fetched from the Page Publisher API at request time (see
// `fetchPublishedPage`), not generated ahead of time, so no paths are
// pre-rendered at build time.
export const getStaticPaths: GetStaticPaths<RouteParams> = async () => ({
  paths: [],
  fallback: 'blocking',
})

export const getStaticProps: GetPageStaticProps = async ({ params, locale }) => {
  const section = params?.section ?? ''

  // `section` must be one of the user-managed sections in the Page Publisher
  // registry (e.g. "guides", "tools"). `middleware.ts` only rewrites
  // registered sections to `/__cms/...`, so this guards direct requests to
  // `/__cms/<anything>/...`.
  if (!(await isValidSection(section))) return { notFound: true }

  const slug = params?.slug?.join('/') ?? ''

  const page = await fetchPublishedPage(section, slug)
  if (!page) return { notFound: true }

  const [author, sectionLabel, layoutProps] = await Promise.all([
    getAuthorById(page.authorId),
    getSectionLabel(section),
    getPublishedPageLayoutProps(locale),
  ])

  return {
    props: {
      page,
      // `undefined` isn't serializable as a prop; omit the key instead.
      ...(author ? { author } : {}),
      ...(sectionLabel ? { sectionLabel } : {}),
      ...layoutProps,
    },
    revalidate: 300,
  }
}
